"use client";

import { useAuth } from "@/contexts/AuthContext";
import { usersAdapter } from "@/lib/adapters/firestore";
import { NotificationPreferences } from "@/types";
import { Switch } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

/** Per-category email opt-out keys managed by this form. */
type EmailPrefKey = "emailChallengeNotifications" | "emailMatchScheduled";

/**
 * Email notification opt-out toggles.
 *
 * Unlike push preferences, these are shown regardless of push-notification
 * support — email delivery is independent of the browser push permission.
 *
 * A master switch (`emailNotificationsEnabled`) turns all transactional emails
 * on/off; when off, the granular toggles are hidden. Everything defaults to ON
 * (opt-out model): a user receives emails unless they explicitly disable them.
 * Granular choices are preserved while the master is off and restored when it
 * is turned back on.
 */
export function EmailNotificationPreferencesForm() {
  const { user, refreshUser } = useAuth();
  const t = useTranslations();
  const [enabled, setEnabled] = useState(true);
  const [preferences, setPreferences] = useState<{
    emailChallengeNotifications: boolean;
    emailMatchScheduled: boolean;
  }>({
    emailChallengeNotifications: true,
    emailMatchScheduled: true,
  });
  // Marketing/newsletter consent — independent of the transactional switches.
  const [marketing, setMarketing] = useState(true);
  const [updating, setUpdating] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user?.notificationPreferences) {
      setEnabled(user.notificationPreferences.emailNotificationsEnabled ?? true);
      setMarketing(user.notificationPreferences.marketingEmailsEnabled ?? true);
      setPreferences({
        emailChallengeNotifications:
          user.notificationPreferences.emailChallengeNotifications ?? true,
        emailMatchScheduled:
          user.notificationPreferences.emailMatchScheduled ?? true,
      });
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (!user) {
    return null;
  }

  const resyncFromUser = () => {
    setEnabled(user.notificationPreferences?.emailNotificationsEnabled ?? true);
    setPreferences({
      emailChallengeNotifications:
        user.notificationPreferences?.emailChallengeNotifications ?? true,
      emailMatchScheduled:
        user.notificationPreferences?.emailMatchScheduled ?? true,
    });
  };

  // Persist a partial preference patch, merged with existing preferences so we
  // never clobber the push toggles (Firestore replaces the whole
  // `notificationPreferences` map on update). Debounced by callers.
  const savePreferences = async (patch: Partial<NotificationPreferences>) => {
    try {
      setUpdating(true);

      const merged: NotificationPreferences = {
        ...user.notificationPreferences,
        ...patch,
      };

      await usersAdapter.update(user.id, {
        notificationPreferences: merged,
      });

      await refreshUser();

      addToast({
        title: t("settings.emailNotifications.success"),
        color: "success",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating email preferences:", error);
      resyncFromUser();
      addToast({
        title: t("settings.emailNotifications.error"),
        color: "danger",
        severity: "danger",
      });
    } finally {
      setUpdating(false);
    }
  };

  const debouncedSave = (patch: Partial<NotificationPreferences>) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      savePreferences(patch);
    }, 800);
  };

  const handleMasterToggle = (value: boolean) => {
    setEnabled(value);
    // Send the full email state so a queued save can't drop an earlier change.
    debouncedSave({ emailNotificationsEnabled: value, ...preferences });
  };

  const handleToggle = (key: EmailPrefKey, value: boolean) => {
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    debouncedSave({ emailNotificationsEnabled: enabled, ...next });
  };

  // Newsletter is a single discrete toggle persisted to Firestore, which is the
  // sole source of truth: the newsletter is sent from a local script that reads
  // this flag at send time. We intentionally do NOT store contacts in Resend, so
  // there is nothing to mirror here.
  const handleNewsletterToggle = async (value: boolean) => {
    setMarketing(value);
    try {
      setUpdating(true);

      const merged: NotificationPreferences = {
        ...user.notificationPreferences,
        marketingEmailsEnabled: value,
      };
      await usersAdapter.update(user.id, { notificationPreferences: merged });

      await refreshUser();
      addToast({
        title: t("settings.emailNotifications.success"),
        color: "success",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating newsletter preference:", error);
      setMarketing(
        user.notificationPreferences?.marketingEmailsEnabled ?? true,
      );
      addToast({
        title: t("settings.emailNotifications.error"),
        color: "danger",
        severity: "danger",
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Master toggle (mirrors the push notifications header) */}
      <div className="flex items-center gap-3">
        <Icon
          icon="solar:letter-bold"
          className="h-5 w-5 text-primary"
          aria-hidden="true"
        />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">
            {t("settings.emailNotifications.title")}
          </p>
          <p className="text-xs text-default-500">
            {t("settings.emailNotifications.description")}
          </p>
        </div>
        <Switch
          size="sm"
          isSelected={enabled}
          onValueChange={handleMasterToggle}
          isDisabled={updating}
          aria-label={t("settings.emailNotifications.title")}
        />
      </div>

      {/* Granular toggles — only when email is enabled */}
      {enabled && (
        <div className="space-y-3 pt-2 border-t border-divider">
          {/* Challenge emails */}
          <div className="flex items-start justify-between gap-4 py-2">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-foreground">
                {t("settings.emailNotifications.challenge.title")}
              </h4>
              <p className="text-xs text-default-400 mt-0.5">
                {t("settings.emailNotifications.challenge.description")}
              </p>
            </div>
            <Switch
              size="sm"
              isSelected={preferences.emailChallengeNotifications}
              onValueChange={(value) =>
                handleToggle("emailChallengeNotifications", value)
              }
              isDisabled={updating}
            />
          </div>

          {/* Match scheduled / rescheduled emails */}
          <div className="flex items-start justify-between gap-4 py-2">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-foreground">
                {t("settings.emailNotifications.matchScheduled.title")}
              </h4>
              <p className="text-xs text-default-400 mt-0.5">
                {t("settings.emailNotifications.matchScheduled.description")}
              </p>
            </div>
            <Switch
              size="sm"
              isSelected={preferences.emailMatchScheduled}
              onValueChange={(value) =>
                handleToggle("emailMatchScheduled", value)
              }
              isDisabled={updating}
            />
          </div>
        </div>
      )}

      {/* Newsletter — independent of the transactional master switch above */}
      <div className="flex items-start justify-between gap-4 py-2 border-t border-divider pt-4">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-foreground">
            {t("settings.emailNotifications.newsletter.title")}
          </h4>
          <p className="text-xs text-default-400 mt-0.5">
            {t("settings.emailNotifications.newsletter.description")}
          </p>
        </div>
        <Switch
          size="sm"
          isSelected={marketing}
          onValueChange={handleNewsletterToggle}
          isDisabled={updating}
          aria-label={t("settings.emailNotifications.newsletter.title")}
        />
      </div>
    </div>
  );
}
